import React, { useEffect, useMemo, useState } from "react";
import api, { getErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Trash2, Plus, X, Check, RefreshCw, Pencil, FileDown, Printer } from "lucide-react";
import { format, parseISO } from "date-fns";

const STAFF_TYPE_OPTIONS = [
  { value: "teacher", label: "Teacher" },
  { value: "assistant_teacher", label: "Assistant Teacher" },
  { value: "admin_staff", label: "Admin Staff" },
];
const GENDER_OPTIONS = [
  { value: "female", label: "Female" }, { value: "male", label: "Male" },
  { value: "other", label: "Other" }, { value: "prefer_not", label: "Prefer not to say" },
];
const EDU_OPTIONS = [
  { value: "high_school", label: "High School" }, { value: "bachelor", label: "Bachelor" },
  { value: "master", label: "Master" }, { value: "phd", label: "Ph.D." },
];
const STATUS_OPTIONS = [
  { value: "active", label: "Active" }, { value: "on_holiday", label: "On Holiday" }, { value: "resigned", label: "Resigned" },
];
const STATUS_TONE = {
  active: "bg-emerald-50 text-emerald-700 border-emerald-200",
  on_holiday: "bg-amber-50 text-amber-700 border-amber-200",
  resigned: "bg-stone-100 text-stone-600 border-stone-300",
};
const EMPTY = {
  name_en: "", name_zh: "", gender: "female", staff_type: "teacher",
  nationality: "United States", username: "", email: "",
  education_level: "bachelor", teaching_certs: [], language_certs: [],
  experience: "", status: "active",
  photo_url: "", public_bio: "", show_on_homepage: true,
  default_wage_rates: [],
};

function MultiToggle({ options, value, onChange, testid }) {
  function toggle(opt) {
    const next = value.includes(opt) ? value.filter(v => v !== opt) : [...value, opt];
    onChange(next);
  }
  return (
    <div className="flex flex-wrap gap-1.5 mt-1" data-testid={testid}>
      {options.map(opt => {
        const active = value.includes(opt);
        return (
          <button key={opt} type="button" onClick={()=>toggle(opt)} data-testid={`${testid}-${opt}`}
            className={`px-2.5 py-1 text-xs rounded-full border transition-all ${active ? "bg-[hsl(var(--primary))] text-white border-[hsl(var(--primary))]" : "bg-white text-[hsl(var(--foreground))] border-[hsl(var(--border))] hover:border-[hsl(var(--primary))]/40"}`}>
            {opt.replace("_", " ")}
          </button>
        );
      })}
    </div>
  );
}

function StaffForm({ value, onChange, constants, isCreate }) {
  const set = (k, v) => onChange({ ...value, [k]: v });
  return (
    <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
      <div className="md:col-span-3"><Label className="text-xs">Name (English)</Label><Input value={value.name_en} onChange={(e)=>set("name_en", e.target.value)} data-testid="staff-form-name-en" className="mt-1" /></div>
      <div className="md:col-span-3"><Label className="text-xs">Name (Chinese)</Label><Input value={value.name_zh || ""} onChange={(e)=>set("name_zh", e.target.value)} data-testid="staff-form-name-zh" className="mt-1" /></div>
      <div className="md:col-span-2"><Label className="text-xs">Gender</Label>
        <Select value={value.gender} onValueChange={(v)=>set("gender", v)}>
          <SelectTrigger data-testid="staff-form-gender" className="mt-1"><SelectValue /></SelectTrigger>
          <SelectContent>{GENDER_OPTIONS.map(o=><SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div className="md:col-span-2"><Label className="text-xs">Staff type</Label>
        <Select value={value.staff_type} onValueChange={(v)=>set("staff_type", v)}>
          <SelectTrigger data-testid="staff-form-type" className="mt-1"><SelectValue /></SelectTrigger>
          <SelectContent>{STAFF_TYPE_OPTIONS.map(o=><SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div className="md:col-span-2"><Label className="text-xs">Status</Label>
        <Select value={value.status} onValueChange={(v)=>set("status", v)}>
          <SelectTrigger data-testid="staff-form-status" className="mt-1"><SelectValue /></SelectTrigger>
          <SelectContent>{STATUS_OPTIONS.map(o=><SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div className="md:col-span-3"><Label className="text-xs">Nationality</Label>
        <Select value={value.nationality} onValueChange={(v)=>set("nationality", v)}>
          <SelectTrigger data-testid="staff-form-nationality" className="mt-1"><SelectValue /></SelectTrigger>
          <SelectContent className="max-h-72">{(constants?.countries || []).map(c=><SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div className="md:col-span-3"><Label className="text-xs">Username</Label><Input value={value.username} onChange={(e)=>set("username", e.target.value.replace(/[^A-Za-z0-9_.-]/g,""))} data-testid="staff-form-username" className="mt-1" /></div>
      <div className="md:col-span-3"><Label className="text-xs">Email</Label><Input type="email" value={value.email} onChange={(e)=>set("email", e.target.value)} data-testid="staff-form-email" className="mt-1" /></div>
      <div className="md:col-span-3"><Label className="text-xs">Education level</Label>
        <Select value={value.education_level} onValueChange={(v)=>set("education_level", v)}>
          <SelectTrigger data-testid="staff-form-education" className="mt-1"><SelectValue /></SelectTrigger>
          <SelectContent>{EDU_OPTIONS.map(o=><SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div className="md:col-span-6"><Label className="text-xs">Teaching certificates (multi-select)</Label>
        <MultiToggle options={constants?.teaching_certs || []} value={value.teaching_certs} onChange={(v)=>set("teaching_certs", v)} testid="staff-form-teaching-certs" />
      </div>
      <div className="md:col-span-6"><Label className="text-xs">Language proficiency (multi-select)</Label>
        <MultiToggle options={constants?.language_certs || []} value={value.language_certs} onChange={(v)=>set("language_certs", v)} testid="staff-form-lang-certs" />
      </div>
      <div className="md:col-span-12"><Label className="text-xs">Relevant experience (free text)</Label>
        <Textarea rows={3} value={value.experience || ""} onChange={(e)=>set("experience", e.target.value)} data-testid="staff-form-experience" className="mt-1" />
      </div>
      <div className="md:col-span-8"><Label className="text-xs">Photo URL (optional — shown on the homepage)</Label>
        <Input value={value.photo_url || ""} onChange={(e)=>set("photo_url", e.target.value)} placeholder="https://…" data-testid="staff-form-photo" className="mt-1" />
      </div>
      <div className="md:col-span-4 flex items-end pb-1"><label className="flex items-center gap-2 text-xs cursor-pointer" data-testid="staff-form-show-toggle">
        <input type="checkbox" checked={!!value.show_on_homepage} onChange={(e)=>set("show_on_homepage", e.target.checked)} className="w-4 h-4 accent-[hsl(var(--primary))]" />
        Show on homepage &ldquo;Meet our teachers&rdquo;
      </label></div>
      <div className="md:col-span-12"><Label className="text-xs">Public bio (≤ 500 chars — falls back to first 150 chars of Experience if empty)</Label>
        <Textarea rows={2} value={value.public_bio || ""} onChange={(e)=>set("public_bio", e.target.value)} data-testid="staff-form-bio" className="mt-1" />
      </div>
      <div className="md:col-span-12">
        <div className="flex items-center justify-between">
          <Label className="text-xs">Default wage rates (per class type — used to pre-fill payslips)</Label>
          <Button type="button" size="sm" variant="outline" onClick={()=>set("default_wage_rates", [...(value.default_wage_rates || []), { class_type: "", rate: 0 }])} className="h-7 text-xs rounded-full" data-testid="staff-form-add-rate">+ Add rate</Button>
        </div>
        <div className="mt-2 space-y-2">
          {(value.default_wage_rates || []).map((r, i) => (
            <div key={i} className="grid grid-cols-12 gap-2 items-center" data-testid={`staff-form-rate-${i}`}>
              <Input value={r.class_type} onChange={(e)=>{ const arr = [...value.default_wage_rates]; arr[i] = { ...arr[i], class_type: e.target.value }; set("default_wage_rates", arr); }} placeholder="e.g. Group / 1-on-1 / IB HL" className="col-span-7 h-9 text-sm" data-testid={`staff-form-rate-${i}-type`} />
              <Input type="number" value={r.rate} onChange={(e)=>{ const arr = [...value.default_wage_rates]; arr[i] = { ...arr[i], rate: Number(e.target.value) || 0 }; set("default_wage_rates", arr); }} placeholder="Rate / hr" className="col-span-4 h-9 text-sm text-right" data-testid={`staff-form-rate-${i}-rate`} />
              <Button type="button" size="sm" variant="ghost" onClick={()=>{ const arr = value.default_wage_rates.filter((_, idx) => idx !== i); set("default_wage_rates", arr); }} className="col-span-1 h-7 w-7 p-0 text-[hsl(var(--destructive))]" data-testid={`staff-form-rate-${i}-remove`}>×</Button>
            </div>
          ))}
          {(value.default_wage_rates || []).length === 0 && <p className="text-xs italic text-[hsl(var(--muted-foreground))]">No saved rates — these will be empty when creating a payslip.</p>}
        </div>
      </div>
      {isCreate && (
        <div className="md:col-span-12 text-xs italic text-[hsl(var(--muted-foreground))] bg-[hsl(var(--muted))]/40 rounded-lg p-3" data-testid="staff-form-default-pw-note">
          Default sign-in password: <span className="font-mono font-semibold">{constants?.default_password || "00000"}</span> — staff will be prompted to change it on first login.
        </div>
      )}
    </div>
  );
}

export default function StaffPanel() {
  const [items, setItems] = useState([]);
  const [constants, setConstants] = useState(null);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null); // staff id
  const [draft, setDraft] = useState(EMPTY);
  const [filter, setFilter] = useState({ staff_type: "all", status: "all" });

  async function load() {
    try { const { data } = await api.get("/admin/staff"); setItems(data); }
    catch (e) { toast.error(getErrorDetail(e)); }
  }
  async function loadConstants() {
    try { const { data } = await api.get("/admin/staff/constants"); setConstants(data); }
    catch (e) { toast.error(getErrorDetail(e)); }
  }
  useEffect(() => { (async () => { await Promise.all([load(), loadConstants()]); })(); }, []);

  const filtered = useMemo(() => items.filter(s =>
    (filter.staff_type === "all" || s.staff_type === filter.staff_type)
    && (filter.status === "all" || s.status === filter.status)
  ), [items, filter]);

  async function create() {
    if (!draft.name_en || !draft.username || !draft.email) { toast.error("Name, username, and email are required"); return; }
    try {
      await api.post("/admin/staff", draft);
      toast.success(`Staff created · default password ${constants?.default_password || "00000"}`);
      setCreating(false); setDraft(EMPTY); load();
    } catch (e) { toast.error(getErrorDetail(e)); }
  }
  async function saveEdit() {
    try {
      await api.patch(`/admin/staff/${editing}`, draft);
      toast.success("Staff updated");
      setEditing(null); setDraft(EMPTY); load();
    } catch (e) { toast.error(getErrorDetail(e)); }
  }
  async function del(id) {
    if (!window.confirm("Delete this staff record?")) return;
    try { await api.delete(`/admin/staff/${id}`); load(); }
    catch (e) { toast.error(getErrorDetail(e)); }
  }
  async function resetPw(id) {
    if (!window.confirm(`Reset password to default (${constants?.default_password || "00000"})?`)) return;
    try { await api.patch(`/admin/staff/${id}`, { reset_password: true }); toast.success("Password reset"); load(); }
    catch (e) { toast.error(getErrorDetail(e)); }
  }
  function startEdit(s) {
    setEditing(s.id);
    setDraft({
      name_en: s.name_en, name_zh: s.name_zh || "", gender: s.gender, staff_type: s.staff_type,
      nationality: s.nationality, username: s.username, email: s.email,
      education_level: s.education_level, teaching_certs: s.teaching_certs || [],
      language_certs: s.language_certs || [], experience: s.experience || "", status: s.status,
      photo_url: s.photo_url || "", public_bio: s.public_bio || "",
      show_on_homepage: s.show_on_homepage !== false,
      default_wage_rates: s.default_wage_rates || [],
    });
    setCreating(false);
  }

  async function downloadBlankForm() {
    try {
      const res = await api.get("/admin/staff/blank-form.docx", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = "Dawnville-Staff-Form.docx";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Blank staff form downloaded");
    } catch (e) { toast.error(getErrorDetail(e)); }
  }

  async function downloadProfile(staff) {
    try {
      const res = await api.get(`/admin/staff/${staff.id}/profile.docx`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document" }));
      const safe = (staff.name_en || "Staff").replace(/[^a-z0-9-_]/gi, "-");
      const a = document.createElement("a");
      a.href = url;
      a.download = `Dawnville-${safe}-Profile.docx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`${staff.name_en}'s profile downloaded`);
    } catch (e) { toast.error(getErrorDetail(e)); }
  }

  return (
    <div data-testid="admin-staff-panel" className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-[10px] uppercase tracking-[0.25em] text-[hsl(var(--muted-foreground))]">Staff directory · {filtered.length} / {items.length}</p>
          <p className="text-xs text-[hsl(var(--muted-foreground))] mt-1">Add teachers, assistants, and admin staff. They sign in at <span className="font-mono">/staff/login</span> using username or email.</p>
        </div>
        <div className="flex items-end gap-2 flex-wrap">
          <Button size="sm" variant="outline" onClick={downloadBlankForm} className="rounded-full border-[hsl(var(--secondary))]/40 text-[hsl(var(--secondary))] hover:bg-[hsl(var(--secondary))]/5" data-testid="staff-download-blank-form">
            <FileDown className="w-3.5 h-3.5 mr-1.5" />Blank form (Word)
          </Button>
          <Select value={filter.staff_type} onValueChange={(v)=>setFilter({...filter, staff_type: v})}>
            <SelectTrigger data-testid="staff-filter-type" className="h-9 w-40 text-xs"><SelectValue placeholder="Type" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All types</SelectItem>{STAFF_TYPE_OPTIONS.map(o=><SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={filter.status} onValueChange={(v)=>setFilter({...filter, status: v})}>
            <SelectTrigger data-testid="staff-filter-status" className="h-9 w-36 text-xs"><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All status</SelectItem>{STATUS_OPTIONS.map(o=><SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
          </Select>
          <Button size="sm" onClick={()=>{ setCreating(v=>!v); setEditing(null); setDraft(EMPTY); }} className="rounded-full bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90 text-white" data-testid="staff-new-toggle">
            {creating ? <><X className="w-3.5 h-3.5 mr-1.5" />Cancel</> : <><Plus className="w-3.5 h-3.5 mr-1.5" />New staff</>}
          </Button>
        </div>
      </div>

      {(creating || editing) && (
        <div className="bg-white border border-[hsl(var(--border))] rounded-2xl p-5 space-y-4" data-testid={creating ? "staff-create-form" : "staff-edit-form"}>
          <StaffForm value={draft} onChange={setDraft} constants={constants} isCreate={creating} />
          <div className="flex justify-end gap-2">
            {editing && <Button variant="outline" onClick={()=>{ setEditing(null); setDraft(EMPTY); }} data-testid="staff-edit-cancel" className="rounded-full">Cancel</Button>}
            <Button onClick={creating ? create : saveEdit} className="rounded-full bg-[hsl(var(--foreground))] text-[hsl(var(--background))] hover:bg-[hsl(var(--foreground))]/90" data-testid={creating ? "staff-create-submit" : "staff-edit-submit"}>
              <Check className="w-4 h-4 mr-1.5" />{creating ? "Create" : "Save"}
            </Button>
          </div>
        </div>
      )}

      <div className="rounded-2xl border border-[hsl(var(--border))] bg-white overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[hsl(var(--muted))]/50">
            <tr className="text-left text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--muted-foreground))]">
              <th className="py-2 px-3">Name</th><th className="py-2 px-3">Type</th><th className="py-2 px-3">Nationality</th>
              <th className="py-2 px-3">Username</th><th className="py-2 px-3">Email</th>
              <th className="py-2 px-3">Education</th><th className="py-2 px-3">Status</th>
              <th className="py-2 px-3">Last login</th><th className="py-2 px-3 w-28"></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(s => (
              <tr key={s.id} data-testid={`staff-row-${s.id}`} className="border-t border-[hsl(var(--border))]/60 hover:bg-[hsl(var(--muted))]/30">
                <td className="py-2 px-3"><div className="font-medium">{s.name_en}</div><div className="text-xs text-[hsl(var(--muted-foreground))]">{s.name_zh || ""}</div></td>
                <td className="py-2 px-3 text-xs">{s.staff_type.replace("_", " ")}</td>
                <td className="py-2 px-3 text-xs">{s.nationality}</td>
                <td className="py-2 px-3 text-xs font-mono">{s.username}</td>
                <td className="py-2 px-3 text-xs">{s.email}</td>
                <td className="py-2 px-3 text-xs">{s.education_level.replace("_", " ")}</td>
                <td className="py-2 px-3 text-xs"><span className={`px-2 py-0.5 rounded-full border ${STATUS_TONE[s.status] || ""}`}>{s.status.replace("_", " ")}</span></td>
                <td className="py-2 px-3 text-xs">{s.last_login_at ? format(parseISO(s.last_login_at), "MMM d, HH:mm") : "—"}</td>
                <td className="py-2 px-3 flex gap-1">
                  <Button size="sm" variant="ghost" onClick={()=>startEdit(s)} data-testid={`staff-edit-${s.id}`} title="Edit"><Pencil className="w-3.5 h-3.5" /></Button>
                  <Button size="sm" variant="ghost" onClick={()=>downloadProfile(s)} data-testid={`staff-profile-${s.id}`} title="Download profile (Word)"><Printer className="w-3.5 h-3.5" /></Button>
                  <Button size="sm" variant="ghost" onClick={()=>resetPw(s.id)} data-testid={`staff-resetpw-${s.id}`} title="Reset password"><RefreshCw className="w-3.5 h-3.5" /></Button>
                  <Button size="sm" variant="ghost" onClick={()=>del(s.id)} data-testid={`staff-del-${s.id}`} className="text-[hsl(var(--destructive))]" title="Delete"><Trash2 className="w-3.5 h-3.5" /></Button>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && <tr><td colSpan={9} className="py-10 text-center text-[hsl(var(--muted-foreground))] text-xs italic">No staff yet — click &ldquo;New staff&rdquo; to add the first one.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
